import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { veritas, fetchAllPages } from '@/lib/supabase';
import { useCurrentUser } from '@/lib/auth';
import { cn, fmtMoney, fmtPct, timeAgo } from '@/lib/utils';
import { Chip, Chips, Panel, PanelBody, PanelHead } from '@/components/ui';
import { useRealtimeInserts } from '@/lib/realtime';
import type { AccountLiveness, AccountSnapshot, AssignableUser } from '@/types/veritas';

// Persisted across reloads so an admin who prefers a specific scope keeps it
// on. Per-user keying isn't required — the dashboard is always rendered for
// the currently signed-in admin and we never expose this state across users.
// Legacy values 'all'/'mine' continue to work; new sentinels 'unassigned' and
// any user UUID extend the type without breaking older localStorage entries.
const SCOPE_LS_KEY = 'veritas.dashboard.scope';
// 'all'         → no owner filter (the historical default)
// 'mine'        → current signed-in admin's owned accounts
// 'unassigned'  → accounts with owner_user_id IS NULL (orphans)
// <uuid>        → a specific user picked from list_assignable_users
type Scope = 'all' | 'mine' | 'unassigned' | string;

// Categories are user-edited per (user, account_login) via the account-detail
// header editor (M2 — tasks/account-user-metadata-spec.md). The dashboard
// filter is purely client-side over rows already loaded by RLS — no extra RPC.
// v1 deliberately did NOT persist the selection; reversed 2026-06-11 with
// Gooseman's sign-off (UI batch, proposal 5c): losing the filter on every
// reload was the bigger surprise. Same localStorage pattern as SCOPE_LS_KEY.
const UNCATEGORIZED = 'Uncategorized';
const CATEGORY_LS_KEY = 'veritas.dashboard.categories.v1';

type Row = AccountLiveness & {
  latest?: Pick<
    AccountSnapshot,
    'balance' | 'equity' | 'credit' | 'dd_pct' | 'day_pnl' | 'currency' | 'broker' | 'ea_version'
  >;
  // Trading-only "Today P&L" from veritas.account_today_pnl. The EA-supplied
  // day_pnl is a balance delta that includes deposits/withdrawals; `trading`
  // strips those out (see the view's migration). `raw`/`ops` are kept for the
  // tooltip so a corrected number is never silently different from the EA's.
  today?: { trading: number; raw: number | null; ops: number };
};

// One row of veritas.account_today_pnl. numeric columns arrive as strings.
type TodayPnlRow = {
  credential_id: string;
  day_pnl_raw: string | number | null;
  day_balance_ops: string | number | null;
  day_pnl_trading: string | number | null;
};

// One row of veritas.list_my_account_scopes(): the viewer's own scope plus each
// grantor (account owner) who shared accounts with them. Powers the non-admin
// "Show" selector — the customer-side analogue of the admin's per-user selector.
type AccountScope = {
  owner_user_id: string;
  owner_email: string | null;
  is_self: boolean;
  account_count: number;
};

export default function DashboardPage() {
  const navigate = useNavigate();
  const { user, loading: authLoading } = useCurrentUser();
  const [rows, setRows] = useState<Row[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  // Admin-only scope toggle. Customers see only their own rows anyway
  // (RLS via security_invoker), so we never render the toggle for them.
  // Default ALL so admins keep the operator-console behaviour they had
  // before this feature existed.
  //
  // Restored value can be any of: 'all' | 'mine' | 'unassigned' | <uuid>.
  // We accept any non-empty string here; if it's a stale UUID (user got
  // removed from list_assignable_users) we fall back to 'all' AFTER the
  // user list lands — see the effect below.
  const [scope, setScope] = useState<Scope>(() => {
    if (typeof window === 'undefined') return 'all';
    const v = localStorage.getItem(SCOPE_LS_KEY);
    return v && v.length > 0 ? (v as Scope) : 'all';
  });
  useEffect(() => {
    try { localStorage.setItem(SCOPE_LS_KEY, scope); } catch { /* private mode */ }
  }, [scope]);

  // Assignable users — populated only for admins (the RPC returns an empty
  // result for non-admins by design, no error). Used both to power the
  // selector dropdown and to render the per-row Owner column. Loaded once
  // on mount; the assignable user list is low-volatility and changing it
  // doesn't need to trip a dashboard refetch.
  const [users, setUsers] = useState<AssignableUser[]>([]);
  useEffect(() => {
    if (authLoading || !user?.isAdmin) return;
    let cancelled = false;
    veritas.rpc('list_assignable_users').then((r) => {
      if (cancelled) return;
      setUsers((r.data ?? []) as AssignableUser[]);
    });
    return () => { cancelled = true; };
  }, [authLoading, user?.isAdmin]);

  // O(1) UUID → email lookup for the Owner column. Built once per `users`
  // change; small enough that a Map is overkill but it keeps the render
  // path branch-free.
  const userEmailById = useMemo(() => {
    const m = new Map<string, string | null>();
    for (const u of users) m.set(u.id, u.email);
    return m;
  }, [users]);

  // Viewer scopes — for NON-admins who have accounts shared with them via
  // account_grants. Mirrors the admin's list_assignable_users but limited to
  // the caller's own visibility (self + grantors). Empty for a plain customer
  // with no shares → the "Show" selector below stays hidden (unchanged UX).
  const [myScopes, setMyScopes] = useState<AccountScope[]>([]);
  useEffect(() => {
    if (authLoading || user?.isAdmin) return;
    let cancelled = false;
    veritas.rpc('list_my_account_scopes').then((r) => {
      if (cancelled) return;
      setMyScopes((r.data ?? []) as AccountScope[]);
    });
    return () => { cancelled = true; };
  }, [authLoading, user?.isAdmin]);
  const sharedScopes = useMemo(() => myScopes.filter((s) => !s.is_self), [myScopes]);
  const hasShared = sharedScopes.length > 0;

  // Non-admin viewers default to their OWN accounts, not everything shared with
  // them. Applied once per browser (V1 flag): if the persisted scope is still
  // the old global default ('all'/'unassigned'), switch it to 'mine'. After
  // this runs, an explicit viewer choice — including "All my access" — persists
  // normally. Admins are untouched (their default stays 'all').
  useEffect(() => {
    if (authLoading || user?.isAdmin) return;
    const MIG_KEY = 'veritas.dashboard.scope.viewerDefaultV1';
    try {
      if (localStorage.getItem(MIG_KEY)) return;
      localStorage.setItem(MIG_KEY, '1');
    } catch { /* private mode — harmless if this re-applies the default */ }
    setScope((s) => (s === 'all' || s === 'unassigned' ? 'mine' : s));
  }, [authLoading, user?.isAdmin]);

  // Stale-UUID guard: if a previously-persisted scope refers to a user that
  // is no longer assignable (user removed, role downgraded, etc.), fall back
  // to 'all'. Runs once the user list has loaded so we don't reset during
  // the bootstrap window. Skipped for non-admins (selector isn't rendered;
  // 'mine' / UUID values are harmless because the filter branches all
  // short-circuit when user.isAdmin is false — but cleaning up keeps the
  // localStorage tidy if the user is ever promoted back).
  useEffect(() => {
    if (scope === 'all' || scope === 'mine') return;
    if (user?.isAdmin) {
      if (scope === 'unassigned' || users.length === 0) return;
      if (!users.some((u) => u.id === scope)) setScope('all');
    } else {
      // Non-admin: a persisted UUID scope must match a current grantor; if the
      // grant was revoked (or 'unassigned' carried over from an admin session)
      // fall back to 'mine' (the viewer default). Wait until scopes land to
      // avoid a bootstrap reset.
      if (myScopes.length === 0) return;
      if (!myScopes.some((s) => s.owner_user_id === scope)) setScope('mine');
    }
  }, [users, myScopes, scope, user?.isAdmin]);

  async function load() {
    setError(null);
    // Sort by account_login ascending — keeps rows in a stable, predictable
    // order so users can find a specific account by number without it
    // jumping around as snapshots arrive. Previously sorted by
    // seconds_since_last (most-recently-active first), which made the
    // list reshuffle every 30 seconds whenever a new snapshot landed.
    // account_liveness has one row PER CREDENTIAL, and an admin in "ALL"
    // scope sees every customer's rows — this list can outgrow the 1000-row
    // server cap as the customer base grows, so it must be paginated.
    // credential_id is the view's unique key (account_login is NOT — two
    // credentials can share one MT5 login), so it's the total-order key.
    const liveRes = await fetchAllPages<AccountLiveness>((from, to) => {
      let q = veritas
        .from('account_liveness')
        .select('*')
        .order('account_login', { ascending: true })
        .order('credential_id', { ascending: true }); // unique tiebreaker — see fetchAllPages contract
      // Owner-scoped filter. For non-admins these branches never fire (the
      // selector isn't rendered) AND the underlying view's RLS already
      // restricts to their owned rows, so the .eq is belt-and-braces only.
      //   'all'         → no narrowing
      //   'mine'        → current admin's owned credentials
      //   'unassigned'  → orphan credentials (no owner)
      //   <uuid>        → specific user's owned credentials
      if (user?.isAdmin) {
        if (scope === 'mine' && user?.id) {
          q = q.eq('owner_user_id', user.id);
        } else if (scope === 'unassigned') {
          q = q.is('owner_user_id', null);
        } else if (scope !== 'all') {
          // Anything else is interpreted as a user UUID. The stale-UUID
          // guard in the effect above swaps a no-longer-assignable UUID
          // back to 'all'; until that runs the query returns zero rows,
          // which is the safer fallback than silently widening to all.
          q = q.eq('owner_user_id', scope);
        }
      } else if (scope !== 'all' && scope !== 'unassigned') {
        // Non-admin viewer with shared accounts: 'mine' → own rows; a grantor
        // UUID → that owner's shared rows. RLS still restricts the result to
        // the caller's visible set (own ∪ granted), so this only narrows
        // within it — it can never widen access.
        if (scope === 'mine' && user?.id) q = q.eq('owner_user_id', user.id);
        else q = q.eq('owner_user_id', scope);
      }
      return q.range(from, to);
    });

    if (liveRes.error) {
      setError(liveRes.error.message);
      setLoading(false);
      return;
    }
    const live = (liveRes.data ?? []) as AccountLiveness[];

    // Trading-only "Today P&L" — single source: veritas.account_today_pnl.
    // The EA-supplied day_pnl is a balance delta that includes deposits/
    // withdrawals; this view subtracts the same-broker-day capital flows so a
    // $10k deposit no longer reads as +$10k of P&L. One batched, paginated,
    // RLS-scoped query keyed by credential_id — the deposit-stripping logic
    // lives in exactly one place (the view), not duplicated per surface.
    const todayRes = await fetchAllPages<TodayPnlRow>((from, to) =>
      veritas
        .from('account_today_pnl')
        .select('credential_id,day_pnl_raw,day_balance_ops,day_pnl_trading')
        .order('credential_id', { ascending: true })
        .range(from, to),
    );
    if (todayRes.error) {
      setError(todayRes.error.message);
      setLoading(false);
      return;
    }
    const todayByCred = new Map<string, NonNullable<Row['today']>>();
    for (const t of (todayRes.data ?? []) as TodayPnlRow[]) {
      todayByCred.set(t.credential_id, {
        trading: Number(t.day_pnl_trading ?? 0),
        raw: t.day_pnl_raw == null ? null : Number(t.day_pnl_raw),
        ops: Number(t.day_balance_ops ?? 0),
      });
    }

    // Latest snapshot per CREDENTIAL now rides along on the account_liveness
    // rows (the view's lateral exposes latest_* fields, ordered received_at
    // DESC — server clock, robust to EA clock skew). No per-credential
    // fan-out query: this removes the N+1 that dominated DB read load.
    const nextRows: Row[] = live.map((l) => ({
      ...l,
      latest: {
        balance:    l.latest_balance,
        equity:     l.latest_equity,
        credit:     l.latest_credit,
        dd_pct:     l.latest_dd_pct,
        day_pnl:    l.latest_day_pnl,
        currency:   l.latest_currency,
        broker:     l.latest_broker,
        ea_version: l.latest_ea_version,
      },
      today: todayByCred.get(l.credential_id),
    }));

    setRows(nextRows);
    setLoading(false);
  }

  // Initial load + a long fallback poll. With realtime enabled (Database →
  // Replication on `account_snapshots`), we'll get push events on every
  // insert, but a 60s safety net keeps the dashboard correct if realtime
  // ever drops (network blip, server restart, etc.).
  // Re-load when scope or user changes — the query depends on both.
  //
  // GUARD: don't fire load() until auth has resolved. On hard refresh the
  // session restore takes a tick; firing a load before user.id is known
  // means the "MINE" filter silently no-ops and we'd race an unfiltered
  // ALL query against the eventual filtered MINE one — whichever lands
  // last wins, which produced the "hard refresh always shows ALL" bug.
  useEffect(() => {
    if (authLoading) return;
    load();
    const id = setInterval(load, 60_000);
    return () => clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scope, user?.id, authLoading]);

  // Debounce burst inserts (192-row backfill, multiple credentials at once)
  // into a single coalesced refresh. Use a ref so the debounce timer survives
  // re-renders.
  //
  // The realtime callback is bound once per channel; without this ref, the
  // captured `load` would be the FIRST render's load (with scope='all'),
  // and toggling to "MINE" would silently flip back to "ALL" the moment
  // the next snapshot arrived. loadRef always points at the latest
  // closure so each push fires a load() with the current scope/user.
  const refreshTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const loadRef = useRef(load);
  useEffect(() => { loadRef.current = load; });
  const scheduleRefresh = useCallback(() => {
    if (refreshTimer.current) clearTimeout(refreshTimer.current);
    refreshTimer.current = setTimeout(() => { loadRef.current(); }, 1500);
  }, []);
  useEffect(() => () => {
    if (refreshTimer.current) clearTimeout(refreshTimer.current);
  }, []);

  // Current rows mirrored into a ref so the realtime handler can test
  // credential membership without tearing down/recreating the channel.
  const rowsRef = useRef<Row[]>([]);
  useEffect(() => { rowsRef.current = rows; }, [rows]);

  // Realtime: patch the affected row IN PLACE on each insert instead of
  // re-running the whole dashboard query. The pushed snapshot already carries
  // the financials, and a fresh insert means the credential is live right now,
  // so we also refresh its status tile locally. This kills the previous
  // reload-on-every-insert loop that, for admins (RLS doesn't narrow), turned
  // the snapshot firehose into a continuous full refetch. RLS still scopes the
  // stream — customers only receive pushes for credentials they own.
  // Unknown credential (new install, or not yet in the loaded set) → fall back
  // to a single coalesced reload to bring the row in. today.trading is
  // deposit-adjusted and only the view computes it; we leave the prior value
  // and let the 60s safety poll reconcile it (latest.day_pnl updates live).
  useRealtimeInserts<AccountSnapshot>(
    'veritas',
    'account_snapshots',
    undefined,
    (snap) => {
      if (!rowsRef.current.some((r) => r.credential_id === snap.credential_id)) {
        scheduleRefresh();
        return;
      }
      setRows((prev) => prev.map((r) =>
        r.credential_id !== snap.credential_id ? r : {
          ...r,
          last_received_at:   snap.received_at,
          last_snapshot_ts:   snap.snapshot_ts,
          seconds_since_last: 0,
          status:             'online' as const,
          latest: {
            balance: snap.balance, equity: snap.equity, credit: snap.credit,
            dd_pct: snap.dd_pct, day_pnl: snap.day_pnl, currency: snap.currency,
            broker: snap.broker, ea_version: snap.ea_version,
          },
        }));
    },
  );

  // Multi-select category filter. Empty set = no filter (all rows visible).
  // Categories come from rows.category; rows with NULL fall into UNCATEGORIZED
  // so users can isolate "not yet labelled" accounts. Restored from
  // localStorage so a reload doesn't wipe the pick; the prune effect below
  // drops entries that no longer exist once rows have actually loaded.
  const [selectedCategories, setSelectedCategories] = useState<Set<string>>(() => {
    if (typeof window === 'undefined') return new Set();
    try {
      const raw = localStorage.getItem(CATEGORY_LS_KEY);
      const arr = raw ? (JSON.parse(raw) as unknown) : null;
      return Array.isArray(arr) ? new Set(arr.filter((c): c is string => typeof c === 'string')) : new Set();
    } catch { return new Set(); }
  });
  useEffect(() => {
    try { localStorage.setItem(CATEGORY_LS_KEY, JSON.stringify([...selectedCategories])); }
    catch { /* private mode */ }
  }, [selectedCategories]);

  // Distinct categories present in the loaded rows, sorted, with
  // UNCATEGORIZED appended when any row lacks a category. We derive the list
  // from rows (not from list_my_account_categories) so filter chips reflect
  // what's actually filterable in the current view.
  const availableCategories = useMemo(() => {
    const named = new Set<string>();
    let hasUncategorized = false;
    for (const r of rows) {
      if (r.category && r.category.trim()) named.add(r.category);
      else hasUncategorized = true;
    }
    const sorted = [...named].sort((a, b) => a.localeCompare(b));
    return hasUncategorized ? [...sorted, UNCATEGORIZED] : sorted;
  }, [rows]);

  // Drop any selected categories that no longer exist in the current rows
  // (e.g. after a re-load that no longer contains them). Keeps the chip
  // group consistent with the actual filter state.
  //
  // Guard: before rows load, availableCategories is [] — pruning then would
  // wipe the localStorage-restored selection on every mount. Any loaded row
  // yields at least UNCATEGORIZED, so "empty list" can only mean "not loaded
  // yet" (or a genuinely empty dashboard, where the filter is moot anyway).
  useEffect(() => {
    if (availableCategories.length === 0) return;
    setSelectedCategories((prev) => {
      const valid = new Set(availableCategories);
      const next = new Set<string>();
      for (const c of prev) if (valid.has(c)) next.add(c);
      return next.size === prev.size ? prev : next;
    });
  }, [availableCategories]);

  const toggleCategory = useCallback((c: string) => {
    setSelectedCategories((prev) => {
      const next = new Set(prev);
      if (next.has(c)) next.delete(c); else next.add(c);
      return next;
    });
  }, []);

  // Rows visible after the category filter. Empty filter = identity.
  const visibleRows = useMemo(() => {
    if (selectedCategories.size === 0) return rows;
    return rows.filter((r) => {
      const key = r.category && r.category.trim() ? r.category : UNCATEGORIZED;
      return selectedCategories.has(key);
    });
  }, [rows, selectedCategories]);

  // Aggregated bar-strip — follows the filter so the strip describes the
  // slice the user is currently looking at.
  //
  // Balances/equity/credit are bucketed PER CURRENCY. Summing 1,000 EUR +
  // 46,410 USD into one scalar "47,410.92" is a self-deception (different
  // units, unrelated quantities) — exactly the class of conflation Veritas is
  // built to call out, not commit. Drawdown is in %, counts are unitless →
  // no bucketing. Rows without a known snapshot currency bucket under '?'
  // so they never silently merge with a known one.
  //
  // Floating P&L is `equity − balance − credit`. MT5/MT4 hold promo capital
  // in ACCOUNT_CREDIT separate from BALANCE; the equity identity is
  //   equity = balance + credit + sum(floating).
  // Pre-v1.04 bridges (and snapshots taken before 2026-05-27) report NULL
  // credit, which we treat as 0 → math collapses to the historical
  // `equity − balance`. Credit-bearing accounts on the new bridge get the
  // correct reading.
  const totals = useMemo(() => {
    const online  = visibleRows.filter((r) => r.status === 'online').length;
    const stale   = visibleRows.filter((r) => r.status === 'stale').length;
    const offline = visibleRows.filter((r) => r.status === 'offline').length;
    const dd      = visibleRows.reduce((a, r) => Math.max(a, r.latest?.dd_pct ?? 0), 0);

    const m = new Map<string, { balance: number; equity: number; credit: number }>();
    for (const r of visibleRows) {
      const ccy = (r.latest?.currency ?? '').trim() || '?';
      const e = m.get(ccy) ?? { balance: 0, equity: 0, credit: 0 };
      // PostgREST returns numeric as STRING (e.g. "1000.0000"). `+=` then
      // string-concats instead of adding. Coerce defensively (matches the
      // existing pattern at line 182 / account-detail 169-178 / 498 / 1089).
      e.balance += Number(r.latest?.balance ?? 0);
      e.equity  += Number(r.latest?.equity  ?? 0);
      e.credit  += Number(r.latest?.credit  ?? 0);
      m.set(ccy, e);
    }
    const byCcy = [...m.entries()]
      .map(([ccy, v]) => ({
        ccy,
        balance: v.balance,
        equity: v.equity,
        credit: v.credit,
        floating: v.equity - v.balance - v.credit,
      }))
      .sort((a, b) => Math.abs(b.balance) - Math.abs(a.balance));

    return { online, stale, offline, dd, byCcy };
  }, [visibleRows]);

  return (
    <div className="space-y-3.5">
      {/* Top stat strip — portfolio summary across every account in view.
          Follows the category filter so the numbers describe what's on screen. */}
      <div className="grid grid-cols-2 gap-3 md:grid-cols-3 xl:grid-cols-5">
        <StatChip
          label="Accounts"
          value={visibleRows.length}
          sub={`${totals.online} online · ${totals.offline} offline`}
        />
        <StatChip
          label="Total balance"
          value={
            <CurrencyStack rows={totals.byCcy.map((c) => ({ ccy: c.ccy, value: c.balance }))} />
          }
          sub="realized only"
        />
        <StatChip
          label="Total equity"
          value={
            <CurrencyStack rows={totals.byCcy.map((c) => ({ ccy: c.ccy, value: c.equity }))} />
          }
          sub={
            <>
              <FloatingStack rows={totals.byCcy} />
              <CreditStack rows={totals.byCcy} />
            </>
          }
        />
        <StatChip
          label="Worst drawdown"
          value={fmtPct(totals.dd)}
          tone={totals.dd > 5 ? 'red' : totals.dd > 1 ? 'amber' : 'default'}
        />
        <StatChip
          label="Online"
          value={`${totals.online} / ${visibleRows.length}`}
          tone="green"
          sub={totals.stale ? `${totals.stale} stale` : 'all reporting'}
        />
      </div>

      <Panel>
        <PanelHead
          title={<>Accounts <span className="text-text-dim">click a row to drill in</span></>}
          right={
            <div className="flex flex-wrap items-center gap-3">
              {/* Admin-only scope selector. Lets the admin (who is also a
                  trader on this platform) flip between operator view
                  ("ALL accounts in the system"), trader view ("only my
                  own"), the orphan bucket, or any specific user's set of
                  accounts. Customers never see this — RLS already scopes
                  them to their own rows. Render as a <select> rather than
                  a chip group because the option count is open-ended
                  (one per user) and a select scales without wrapping the
                  header. */}
              {user?.isAdmin && (
                <div className="flex items-center gap-2 text-[10px] uppercase tracking-eyebrow text-text-dim">
                  <span>User</span>
                  <select
                    value={scope}
                    onChange={(e) => setScope(e.target.value as Scope)}
                    className="num rounded-md border border-line bg-bg-1 px-2 py-1 text-[11px] text-text"
                    title="Filter accounts by owner"
                  >
                    <option value="all">All users</option>
                    <option value="mine">Mine{user.email ? ` (${user.email})` : ''}</option>
                    <option value="unassigned">Unassigned</option>
                    {users.length > 0 && (
                      <optgroup label="Specific user">
                        {users
                          .slice()
                          .sort((a, b) => (a.email ?? a.id).localeCompare(b.email ?? b.id))
                          .map((u) => (
                            <option key={u.id} value={u.id}>
                              {u.email ?? u.id.slice(0, 8)}
                            </option>
                          ))}
                      </optgroup>
                    )}
                  </select>
                </div>
              )}
              {/* Viewer scope selector — the customer-side analogue of the admin
                  selector above. Shown only to non-admins who have at least one
                  account shared with them, so a plain customer's UX is unchanged.
                  Lets a viewer separate their OWN accounts from accounts another
                  user has given them read-only access to (grouped by that owner). */}
              {!user?.isAdmin && hasShared && (
                <div className="flex items-center gap-2 text-[10px] uppercase tracking-eyebrow text-text-dim">
                  <span>Show</span>
                  <select
                    value={scope === 'unassigned' ? 'all' : scope}
                    onChange={(e) => setScope(e.target.value as Scope)}
                    className="num rounded-md border border-line bg-bg-1 px-2 py-1 text-[11px] text-text"
                    title="Switch between your accounts and accounts shared with you"
                  >
                    <option value="all">All my access</option>
                    <option value="mine">My accounts{user?.email ? ` (${user.email})` : ''}</option>
                    <optgroup label="Shared with me">
                      {sharedScopes
                        .slice()
                        .sort((a, b) =>
                          (a.owner_email ?? a.owner_user_id).localeCompare(b.owner_email ?? b.owner_user_id),
                        )
                        .map((s) => (
                          <option key={s.owner_user_id} value={s.owner_user_id}>
                            {(s.owner_email ?? s.owner_user_id.slice(0, 8))} ({s.account_count})
                          </option>
                        ))}
                    </optgroup>
                  </select>
                </div>
              )}
              <span className="text-[11px] text-text-dim">
                {loading
                  ? 'Loading…'
                  : selectedCategories.size > 0
                    ? `${visibleRows.length} of ${rows.length} credentials · refreshed every 15s`
                    : `${rows.length} credentials · refreshed every 15s`}
              </span>
            </div>
          }
        />
        <PanelBody flush>
          {error && (
            <div className="m-3.5 rounded border border-red/40 bg-red/10 px-3 py-2 text-sm text-red">
              {error}
            </div>
          )}

          {/* Category filter — multi-select chip group. Hidden unless the
              loaded rows actually have more than one category bucket (any
              named category or any uncategorized row), so the filter doesn't
              appear before the user has done any labelling. Filter is
              client-side over the already-loaded rows. */}
          {availableCategories.length > 1 && (
            <div className="flex flex-wrap items-center gap-2 border-b border-line-soft px-4 py-3">
              <span className="text-[10px] uppercase tracking-eyebrow text-text-dim">Category</span>
              <Chips>
                {availableCategories.map((c) => (
                  <Chip key={c} active={selectedCategories.has(c)} onClick={() => toggleCategory(c)}>
                    {c}
                  </Chip>
                ))}
              </Chips>
              {selectedCategories.size > 0 && (
                <button
                  onClick={() => setSelectedCategories(new Set())}
                  className="ml-1 text-[10px] uppercase tracking-eyebrow text-text-dim underline decoration-dotted underline-offset-4 hover:text-green"
                >
                  Clear
                </button>
              )}
            </div>
          )}

          {/* ── Mobile: stacked cards ───────────────────────────────────
              Phones can't horizontal-scroll an 8-column table comfortably;
              cards put status + account number up top, then a 2-column
              KPI grid for the four numbers that matter. md+ stays as the
              dense table below. */}
          <div className="md:hidden">
            {rows.length === 0 && !loading && (
              <div className="px-4 py-12 text-center text-[12px] text-text-dim">
                No credentials yet. Issue one on the Credentials tab, or run
                the seed file to prefill demo data.
              </div>
            )}
            {rows.length > 0 && visibleRows.length === 0 && (
              <div className="px-4 py-12 text-center text-[12px] text-text-dim">
                No accounts match the current category filter.{' '}
                <button
                  onClick={() => setSelectedCategories(new Set())}
                  className="underline decoration-dotted underline-offset-4 hover:text-green"
                >
                  Clear filter
                </button>
              </div>
            )}
            <ul className="divide-y divide-line-soft">
              {visibleRows.map((r) => {
                const ddPct = r.latest?.dd_pct ?? 0;
                // Trading-only Today P&L (deposits/withdrawals stripped). Fall
                // back to the raw EA value only if the view row is missing.
                const dayPnl = r.today?.trading ?? r.latest?.day_pnl ?? 0;
                const ccy = r.latest?.currency ?? '';
                return (
                  <li
                    key={r.credential_id}
                    onClick={() => navigate(`/accounts/${r.account_login}`)}
                    className="cursor-pointer px-4 py-4 transition-colors active:bg-white/[0.04]"
                  >
                    {/* Top row: status + account + last seen.
                        Title = user's display_label || account number.
                        install_label + broker are demoted to the subtitle so
                        the user's own organization scheme leads. */}
                    <div className="mb-3 flex items-center gap-3">
                      <StatusDot status={r.status} />
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <div className="truncate text-[14px] font-medium text-text">
                            {(r.display_label && r.display_label.trim()) || r.account_login}
                          </div>
                          {r.category && (
                            <span className="rounded-full border border-line bg-bg-2 px-1.5 py-[1px] text-[9px] font-semibold uppercase tracking-eyebrow text-text-mid">
                              {r.category}
                            </span>
                          )}
                        </div>
                        <div className="truncate text-[10.5px] text-text-dim">
                          {r.display_label?.trim() ? `${r.account_login} · ` : ''}
                          {r.install_label} · {r.latest?.broker ?? '—'}
                          {user?.isAdmin && (
                            <>
                              {' · '}
                              {r.owner_user_id
                                ? (userEmailById.get(r.owner_user_id) ?? r.owner_user_id.slice(0, 8))
                                : <span className="italic">unassigned</span>}
                            </>
                          )}
                        </div>
                      </div>
                      <div className="shrink-0 text-right text-[10.5px] text-text-dim">
                        {timeAgo(r.last_received_at)}
                      </div>
                    </div>

                    {/* KPI grid — Balance / Equity / DD / Today P&L */}
                    <div className="grid grid-cols-2 gap-2">
                      <CardKpi label="Balance" value={fmtMoney(r.latest?.balance, ccy)} />
                      <CardKpi label="Equity"  value={fmtMoney(r.latest?.equity, ccy)} />
                      <CardKpi
                        label="Drawdown"
                        value={fmtPct(r.latest?.dd_pct)}
                        valueClass={
                          ddPct > 5 ? 'text-red'
                          : ddPct > 1 ? 'text-amber'
                          : 'text-text'
                        }
                      />
                      <CardKpi
                        label="Today P&L"
                        value={fmtMoney(dayPnl, ccy)}
                        valueClass={
                          dayPnl > 0 ? 'text-green'
                          : dayPnl < 0 ? 'text-red'
                          : 'text-text-dim'
                        }
                      />
                    </div>
                  </li>
                );
              })}
            </ul>
          </div>

          {/* ── md+: dense desktop table ─────────────────────────────── */}
          <div className="thin-scroll hidden overflow-auto md:block">
            <table className="w-full border-collapse text-[12px]">
              <thead>
                <tr>
                  <Th>Status</Th>
                  <Th>Account</Th>
                  {user?.isAdmin && <Th>Owner</Th>}
                  <Th>Balance</Th>
                  <Th>Equity</Th>
                  <Th>Drawdown</Th>
                  <Th>Today P&amp;L</Th>
                  <Th>Broker</Th>
                  <Th>Last seen</Th>
                </tr>
              </thead>
              <tbody>
                {rows.length === 0 && !loading && (
                  <tr>
                    <td colSpan={user?.isAdmin ? 9 : 8} className="px-4 py-12 text-center text-text-dim">
                      No credentials yet. Issue one on the Credentials tab, or run the seed file
                      to prefill demo data.
                    </td>
                  </tr>
                )}
                {rows.length > 0 && visibleRows.length === 0 && (
                  <tr>
                    <td colSpan={user?.isAdmin ? 9 : 8} className="px-4 py-12 text-center text-text-dim">
                      No accounts match the current category filter.{' '}
                      <button
                        onClick={() => setSelectedCategories(new Set())}
                        className="underline decoration-dotted underline-offset-4 hover:text-green"
                      >
                        Clear filter
                      </button>
                    </td>
                  </tr>
                )}
                {visibleRows.map((r) => (
                  <tr
                    key={r.credential_id}
                    onClick={() => navigate(`/accounts/${r.account_login}`)}
                    className="cursor-pointer border-b border-line-soft transition-colors hover:bg-white/[0.02]"
                  >
                    <td className="px-4 py-3"><StatusDot status={r.status} /></td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        <span className="font-medium text-text">
                          {(r.display_label && r.display_label.trim()) || r.account_login}
                        </span>
                        {r.category && (
                          <span className="rounded-full border border-line bg-bg-2 px-1.5 py-[1px] text-[9px] font-semibold uppercase tracking-eyebrow text-text-mid">
                            {r.category}
                          </span>
                        )}
                      </div>
                      <div className="text-[10px] text-text-dim">
                        {r.display_label?.trim() ? `${r.account_login} · ` : ''}
                        {r.install_label}
                      </div>
                    </td>
                    {user?.isAdmin && (
                      <td
                        className="px-4 py-3 text-[11px] text-text-mid"
                        onClick={(e) => {
                          // Don't drill into the account when the admin
                          // clicks the owner email — let them quickly pivot
                          // the whole dashboard to that user instead.
                          if (!r.owner_user_id) return;
                          e.stopPropagation();
                          setScope(r.owner_user_id);
                        }}
                        title={r.owner_user_id ? 'Click to filter dashboard to this user' : 'Unassigned'}
                      >
                        {r.owner_user_id ? (
                          <span className="cursor-pointer underline decoration-dotted underline-offset-4 hover:text-green">
                            {userEmailById.get(r.owner_user_id) ?? r.owner_user_id.slice(0, 8)}
                          </span>
                        ) : (
                          <span className="text-text-dim italic">unassigned</span>
                        )}
                      </td>
                    )}
                    <td className="num px-4 py-3 text-[11.5px]">{fmtMoney(r.latest?.balance, r.latest?.currency ?? '')}</td>
                    <td className="num px-4 py-3 text-[11.5px]">{fmtMoney(r.latest?.equity, r.latest?.currency ?? '')}</td>
                    <td className="num px-4 py-3 text-[11.5px]">
                      <span className={cn(
                        (r.latest?.dd_pct ?? 0) > 5 ? 'text-red'
                        : (r.latest?.dd_pct ?? 0) > 1 ? 'text-amber'
                        : 'text-text',
                      )}>{fmtPct(r.latest?.dd_pct)}</span>
                    </td>
                    <td
                      className={cn(
                        'num px-4 py-3 text-[11.5px]',
                        (r.today?.trading ?? r.latest?.day_pnl ?? 0) > 0 ? 'text-green'
                        : (r.today?.trading ?? r.latest?.day_pnl ?? 0) < 0 ? 'text-red'
                        : 'text-text-dim',
                      )}
                      title={
                        r.today && r.today.ops !== 0
                          ? `Trading P&L only — excludes ${fmtMoney(r.today.ops, '')} of deposits/withdrawals booked today (EA raw: ${fmtMoney(r.today.raw ?? 0, '')}).`
                          : undefined
                      }
                    >
                      {fmtMoney(r.today?.trading ?? r.latest?.day_pnl, r.latest?.currency ?? '')}
                    </td>
                    <td className="px-4 py-3 text-[11.5px] text-text-mid">{r.latest?.broker ?? '—'}</td>
                    <td className="px-4 py-3 text-[11.5px] text-text-dim">{timeAgo(r.last_received_at)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </PanelBody>
      </Panel>
    </div>
  );
}

// ────────────────────────────────────────────────────────────────────────
function StatChip({
  label,
  value,
  sub,
  tone = 'default',
}: {
  label: string;
  value: React.ReactNode;
  sub?: React.ReactNode;
  tone?: 'default' | 'green' | 'red' | 'amber';
}) {
  const tColor = tone === 'green' ? 'text-green' : tone === 'red' ? 'text-red' : tone === 'amber' ? 'text-amber' : 'text-text';
  return (
    <div className="rounded-[10px] border border-line bg-bg-1 px-4 py-3.5">
      <div className="text-[9.5px] font-bold uppercase tracking-eyebrow text-text-dim">{label}</div>
      <div className={cn('num mt-1.5 text-[20px] font-bold leading-none', tColor)}>{value}</div>
      {sub && <div className="mt-1.5 text-[10.5px] text-text-dim">{sub}</div>}
    </div>
  );
}

// Per-currency value stack used in the Total Balance / Total Equity cards.
// Single-currency fleets render exactly as before (one big bold number with
// the currency suffix); mixed-currency fleets get one line per currency,
// shrunk slightly so the card still fits. Sums across mixed currencies are
// never produced — that's the bug this replaces.
function CurrencyStack({ rows }: { rows: { ccy: string; value: number }[] }) {
  if (rows.length === 0) return <span className="num">—</span>;
  if (rows.length === 1) {
    return <span className="num">{fmtMoney(rows[0].value, rows[0].ccy)}</span>;
  }
  return (
    <div className="flex flex-col gap-0.5 text-[15px]">
      {rows.map((r) => (
        <span key={r.ccy} className="num">{fmtMoney(r.value, r.ccy)}</span>
      ))}
    </div>
  );
}

// Non-withdrawable credit pill, rendered beneath the Total Equity card. MT5/MT4
// hold promo / bonus capital in ACCOUNT_CREDIT, separate from BALANCE — it
// counts toward equity (you can trade against it) but you can't withdraw it
// and the broker can claw it back. Hiding this would be the same self-
// deception the L3 thesis refuses ("see yourself clearly"). Pill is dropped
// when every bucket's credit is effectively zero so single-account fleets
// don't get cosmetic noise.
function CreditStack({
  rows,
}: {
  rows: { ccy: string; credit: number }[];
}) {
  const nz = rows.filter((r) => Math.abs(r.credit) >= 0.005);
  if (nz.length === 0) return null;
  return (
    <span className="num mt-0.5 inline-flex flex-wrap gap-x-2 gap-y-0.5 text-amber">
      {nz.map((r) => (
        <span key={r.ccy} title="Non-withdrawable promo / bonus capital. Broker can revoke at any time.">
          +{fmtMoney(r.credit, r.ccy)} credit
        </span>
      ))}
    </span>
  );
}

// Floating-P&L breakdown beneath the Total Equity card. One pill per currency
// with non-zero floating, coloured by sign so a green EUR + red USD don't get
// averaged into a misleading neutral. Currencies whose floating is effectively
// zero (|x| < 0.005) are dropped to keep the card uncluttered.
function FloatingStack({
  rows,
}: {
  rows: { ccy: string; floating: number }[];
}) {
  const nz = rows.filter((r) => Math.abs(r.floating) >= 0.005);
  if (nz.length === 0) {
    return <span className="text-text-dim">no floating</span>;
  }
  return (
    <span className="num inline-flex flex-wrap gap-x-2 gap-y-0.5">
      {nz.map((r) => (
        <span
          key={r.ccy}
          className={
            r.floating > 0 ? 'text-green'
            : r.floating < 0 ? 'text-red'
            : 'text-text-dim'
          }
        >
          {r.floating > 0 ? '+' : ''}{fmtMoney(r.floating, r.ccy)} floating
        </span>
      ))}
    </span>
  );
}

function Th({ children }: { children: React.ReactNode }) {
  return (
    <th className="sticky top-0 z-[1] whitespace-nowrap border-b border-line bg-bg-2 px-4 py-2.5 text-left text-[9.5px] font-bold uppercase tracking-eyebrow text-text-dim">
      {children}
    </th>
  );
}

// Compact KPI used inside the mobile-card row. Smaller than the page-level
// StatChip (no border, less padding) so four of them fit two-up below the
// account header without crowding.
function CardKpi({
  label,
  value,
  valueClass,
}: {
  label: string;
  value: React.ReactNode;
  valueClass?: string;
}) {
  return (
    <div className="rounded-md bg-bg-2 px-3 py-2.5">
      <div className="text-[9px] font-bold uppercase tracking-eyebrow text-text-dim">
        {label}
      </div>
      <div className={cn('num mt-0.5 text-[13px] font-semibold tabular-nums', valueClass ?? 'text-text')}>
        {value}
      </div>
    </div>
  );
}

function StatusDot({ status }: { status: AccountLiveness['status'] }) {
  const map = {
    online:  { dot: 'bg-green', label: 'Online' },
    stale:   { dot: 'bg-amber', label: 'Stale' },
    offline: { dot: 'bg-text-faint', label: 'Offline' },
  } as const;
  const s = map[status];
  return (
    <span className="inline-flex items-center gap-2">
      <span className={cn('h-2 w-2 rounded-full', s.dot)} />
      <span className="text-[10px] uppercase tracking-eyebrow text-text-mid">{s.label}</span>
    </span>
  );
}
